import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

import ProjectHero from "../../components/ProjectHero";

const FIREBASE_URL =
  "https://portfolio-react-92097-default-rtdb.europe-west1.firebasedatabase.app/.json";

const normalizeProjects = (data) => {
  if (!data) {
    return [];
  }

  if (data.error) {
    throw new Error(data.error);
  }

  if (Array.isArray(data)) {
    return data
      .filter(Boolean)
      .map((project, index) => ({
        id: project.id || String(index),
        ...project,
      }));
  }

  if (data.projects) {
    if (Array.isArray(data.projects)) {
      return data.projects
        .filter(Boolean)
        .map((project, index) => ({
          id: project.id || String(index),
          ...project,
        }));
    }

    if (typeof data.projects === "object") {
      return Object.entries(data.projects)
        .filter(
          ([, project]) =>
            project && typeof project === "object"
        )
        .map(([id, project]) => ({
          id,
          ...project,
        }));
    }
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .filter(([, project]) => {
        return (
          project &&
          typeof project === "object" &&
          (project.title ||
            project.description ||
            project.tags ||
            project.categories ||
            project.images)
        );
      })
      .map(([id, project]) => ({
        id,
        ...project,
      }));
  }

  return [];
};

const getProjectDateValue = (date) => {
  if (!date) {
    return 0;
  }

  const numericDate = Number(date);

  if (!Number.isNaN(numericDate)) {
    return numericDate;
  }

  const parsedDate = new Date(date).getTime();

  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

const Index = () => {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Featured is now the default sorting mode.
  const [sortOrder, setSortOrder] =
    useState("featured");

  const [selectedTag, setSelectedTag] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get(FIREBASE_URL);

        console.log(
          "Firebase response:",
          response.data
        );

        const normalizedProjects = normalizeProjects(
          response.data
        );

        console.log(
          "Normalized projects:",
          normalizedProjects
        );

        setProjects(normalizedProjects);
      } catch (requestError) {
        console.error(
          "Failed to load projects:",
          requestError
        );

        setError(
          requestError?.response?.data?.error ||
            requestError?.message ||
            "The projects could not be loaded. Please refresh the page."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const tagOptions = useMemo(() => {
    const allTags = projects.flatMap((project) => {
      return Array.isArray(project.tags)
        ? project.tags
        : [];
    });

    return [...new Set(allTags)].sort(
      (first, second) =>
        String(first).localeCompare(String(second))
    );
  }, [projects]);

  const categoryOptions = useMemo(() => {
    const allCategories = projects.flatMap(
      (project) => {
        return Array.isArray(project.categories)
          ? project.categories
          : [];
      }
    );

    return [...new Set(allCategories)].sort(
      (first, second) =>
        String(first).localeCompare(String(second))
    );
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    const matchingProjects = projects.filter(
      (project) => {
        const title = String(
          project.title || ""
        ).toLowerCase();

        const shortDescription = String(
          project.shortDescription || ""
        ).toLowerCase();

        const description = String(
          project.description || ""
        ).toLowerCase();

        const problem = String(
          project.problem || ""
        ).toLowerCase();

        const solution = String(
          project.solution || ""
        ).toLowerCase();

        const role = String(
          project.role || ""
        ).toLowerCase();

        const tags = Array.isArray(project.tags)
          ? project.tags.map((tag) =>
              String(tag).toLowerCase()
            )
          : [];

        const categories = Array.isArray(
          project.categories
        )
          ? project.categories.map((category) =>
              String(category).toLowerCase()
            )
          : [];

        const highlights = Array.isArray(
          project.highlights
        )
          ? project.highlights.map((highlight) =>
              String(highlight).toLowerCase()
            )
          : [];

        const matchesSearch =
          !normalizedSearch ||
          title.includes(normalizedSearch) ||
          shortDescription.includes(
            normalizedSearch
          ) ||
          description.includes(normalizedSearch) ||
          problem.includes(normalizedSearch) ||
          solution.includes(normalizedSearch) ||
          role.includes(normalizedSearch) ||
          tags.some((tag) =>
            tag.includes(normalizedSearch)
          ) ||
          categories.some((category) =>
            category.includes(normalizedSearch)
          ) ||
          highlights.some((highlight) =>
            highlight.includes(normalizedSearch)
          );

        const matchesTag =
          !selectedTag ||
          (Array.isArray(project.tags) &&
            project.tags.includes(selectedTag));

        const matchesCategory =
          !selectedCategory ||
          (Array.isArray(project.categories) &&
            project.categories.includes(
              selectedCategory
            ));

        return (
          matchesSearch &&
          matchesTag &&
          matchesCategory
        );
      }
    );

    return [...matchingProjects].sort(
      (firstProject, secondProject) => {
        const firstDate = getProjectDateValue(
          firstProject.date
        );

        const secondDate = getProjectDateValue(
          secondProject.date
        );

        if (sortOrder === "featured") {
          const firstFeatured =
            firstProject.featured === true;

          const secondFeatured =
            secondProject.featured === true;

          // Featured projects appear first.
          if (firstFeatured !== secondFeatured) {
            return firstFeatured ? -1 : 1;
          }

          // Within featured and non-featured groups,
          // show the newest projects first.
          return secondDate - firstDate;
        }

        if (sortOrder === "asc") {
          return firstDate - secondDate;
        }

        // Most recent.
        return secondDate - firstDate;
      }
    );
  }, [
    projects,
    searchTerm,
    selectedTag,
    selectedCategory,
    sortOrder,
  ]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTag("");
    setSelectedCategory("");
    setSortOrder("featured");
  };

  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    selectedTag !== "" ||
    selectedCategory !== "" ||
    sortOrder !== "featured";

  return (
    <main className="min-h-screen bg-zinc-50 text-zinc-950">
      {/* Page header and project filters */}
      <section className="border-b border-zinc-200 bg-white px-6 py-20 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
            className="max-w-3xl"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-indigo-600">
              Selected work
            </p>

            <h1 className="mt-4 text-4xl font-semibold tracking-tight text-zinc-950 sm:text-5xl lg:text-6xl">
              Projects built around real problems
            </h1>

            <p className="mt-6 text-lg leading-8 text-zinc-600">
              A collection of software projects covering
              backend development, full-stack
              applications, cloud systems, data,
              security, and AI.
            </p>
          </motion.div>

          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.15,
              duration: 0.6,
            }}
            className="mt-12 rounded-2xl border border-zinc-200 bg-zinc-50 p-5 shadow-sm"
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[1fr_auto_auto_auto]">
              {/* Search */}
              <div>
                <label
                  htmlFor="project-search"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Search projects
                </label>

                <div className="relative">
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
                  >
                    <path
                      d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>

                  <input
                    id="project-search"
                    type="search"
                    placeholder="Search title, technology, or category"
                    value={searchTerm}
                    onChange={(event) =>
                      setSearchTerm(event.target.value)
                    }
                    className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-12 pr-4 text-zinc-950 outline-none transition placeholder:text-zinc-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                  />
                </div>
              </div>

              {/* Technology filter */}
              <div>
                <label
                  htmlFor="tag-filter"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Technology
                </label>

                <select
                  id="tag-filter"
                  value={selectedTag}
                  onChange={(event) =>
                    setSelectedTag(event.target.value)
                  }
                  className="w-full min-w-48 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                >
                  <option value="">
                    All technologies
                  </option>

                  {tagOptions.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project type filter */}
              <div>
                <label
                  htmlFor="category-filter"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Project type
                </label>

                <select
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(event) =>
                    setSelectedCategory(
                      event.target.value
                    )
                  }
                  className="w-full min-w-48 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                >
                  <option value="">
                    All project types
                  </option>

                  {categoryOptions.map((category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label
                  htmlFor="sort-order"
                  className="mb-2 block text-sm font-medium text-zinc-700"
                >
                  Sort by
                </label>

                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(event) =>
                    setSortOrder(event.target.value)
                  }
                  className="w-full min-w-40 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                >
                  <option value="featured">
                    Featured
                  </option>

                  <option value="desc">
                    Most recent
                  </option>

                  <option value="asc">
                    Oldest first
                  </option>
                </select>
              </div>
            </div>

            {/* Result count and clear button */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 pt-4">
              <p className="text-sm text-zinc-500">
                {isLoading
                  ? "Loading projects..."
                  : `${filteredProjects.length} ${
                      filteredProjects.length === 1
                        ? "project"
                        : "projects"
                    } found`}
              </p>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm font-semibold text-indigo-600 transition hover:text-indigo-800"
                >
                  Clear filters
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project results */}
      <section className="px-6 py-16 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          {/* Loading placeholders */}
          {isLoading && (
            <div className="grid gap-10">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-96 animate-pulse rounded-3xl border border-zinc-200 bg-white"
                />
              ))}
            </div>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
              <h2 className="font-semibold">
                Unable to load projects
              </h2>

              <p className="mt-2">{error}</p>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
                className="mt-5 rounded-xl bg-red-700 px-5 py-3 font-semibold text-white transition hover:bg-red-800"
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty results */}
          {!isLoading &&
            !error &&
            filteredProjects.length === 0 && (
              <div className="rounded-3xl border border-zinc-200 bg-white px-6 py-20 text-center shadow-sm">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-500">
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <h2 className="mt-5 text-2xl font-semibold text-zinc-950">
                  No projects found
                </h2>

                <p className="mt-3 text-zinc-600">
                  Try changing the search term,
                  technology, or project type.
                </p>

                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-7 rounded-xl bg-zinc-950 px-5 py-3 font-semibold text-white transition hover:bg-zinc-800"
                >
                  Clear filters
                </button>
              </div>
            )}

          {/* Project cards */}
          {!isLoading &&
            !error &&
            filteredProjects.length > 0 && (
              <div className="grid gap-10">
                {filteredProjects.map(
                  (project, index) => (
                    <ProjectHero
                      key={
                        project.id ||
                        project.slug ||
                        project.title ||
                        index
                      }
                      project={project}
                      index={index}
                    />
                  )
                )}
              </div>
            )}
        </div>
      </section>
    </main>
  );
};

export default Index;

